<template>
  <div class="app-shell">
    <AuthGate />
    <LicenseGate />
    <AppSidebar />
    <div class="sidebar-overlay" :class="{ visible: uiStore.mobileMenuOpen }" @click="handleMainClick"></div>
    <main class="main-area" @click="handleMainClick">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <ChatPanel />
    <MobileControls />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useUIStore } from './stores/ui';
import { useAuthStore } from './stores/auth';
import { useChatStore } from './stores/chat';
import AppSidebar from './components/AppSidebar.vue';
import ChatPanel from './components/ChatPanel.vue';
import MobileControls from './components/MobileControls.vue';
import AuthGate from './components/AuthGate.vue';
import LicenseGate from './components/LicenseGate.vue';

const route = useRoute();
const authStore = useAuthStore();
const uiStore = useUIStore();
const chatStore = useChatStore();

const ROUTE_SCOPE_MAP = {
  '/': 'overview',
  '/gold': 'gold',
  '/silver': 'silver',
  '/copper': 'copper',
};

const syncScopeFromRoute = (path) => {
  const scope = ROUTE_SCOPE_MAP[path] || 'overview';
  if (chatStore.config.scope !== scope) {
    chatStore.setConfig({ scope });
  }
};

watch(() => route.path, syncScopeFromRoute);

const handleResize = () => {
  uiStore.setMobile(window.innerWidth <= 1100);
};

const handleMainClick = () => {
  if (uiStore.isMobile && uiStore.mobileMenuOpen) {
    uiStore.closeMobileMenu();
  }
};

onMounted(async () => {
  document.body.classList.add('theme-gold');
  await authStore.checkAuthStatus();
  syncScopeFromRoute(route.path);
  handleResize();
  window.addEventListener('resize', handleResize);
  // Always load chat state — uses Supabase if authenticated, localStorage otherwise
  chatStore.loadFromServer();
  // Reload chat state when user returns to the tab/app after navigating away
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    chatStore.loadFromServer();
  }
};
</script>

<style>
/* Route transition only — all layout styles come from themes.css */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
